import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import CustomerPortalLayout from "components/storefront/CustomerPortalLayout";
import {
  PortalAlert,
  PortalButton,
  PortalLoading,
  PortalStatusChip,
  PortalTable,
} from "components/portal/PortalUi";
import { useAuth } from "context/AuthContext";
import { get } from "lib/api";
import { useCurrency } from "context/CurrencyContext";
import { isCustomerRole, isDistributorRole } from "lib/portalRoles";
import useStorefrontCopy from "hooks/useStorefrontCopy";

export default function CustomerOrders() {
  const { token, pluginRole, authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { formatUSDForDisplay } = useCurrency();
  const { t } = useStorefrontCopy();

  const role = String(pluginRole || "").toLowerCase();
  const canViewOrders = isCustomerRole(role) || isDistributorRole(role);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!token || !canViewOrders) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await get("/api/storefront/orders", { pagination: { page: 1, pageSize: 20 } });
        setOrders(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        const msg = e?.response?.data?.error?.message || e?.message || "Failed to load orders";
        setError(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, canViewOrders]);

  if (!token) return <Navigate to="/authentication/sign-in" state={{ from: location }} replace />;
  if (authLoading || pluginRole == null) return null;
  if (!canViewOrders) return <Navigate to="/dashboard" replace />;

  const tableRows = orders.map((o) => {
    const id = o?.documentId || o?.id;
    return {
      key: String(id),
      data: {
        order: o?.salesOrderNumber || id,
        status: String(o?.status || "").trim() || "—",
        payment: String(o?.paymentStatus || "").trim() || "—",
        total: o?.total != null ? formatUSDForDisplay(Number(o.total)) : "—",
        tracking: o?.trackingNumber ? String(o.trackingNumber) : "—",
        documentId: id,
      },
    };
  });

  return (
    <CustomerPortalLayout
      title={t("orders.title", "Orders")}
      subtitle={t("orders.subtitle", "Track your orders and download invoices.")}
    >
      {error ? <PortalAlert severity="error">{error}</PortalAlert> : null}
      {loading ? (
        <PortalLoading label={t("common.loading", "Loading…")} />
      ) : (
        <PortalTable
          emptyLabel={t("orders.noOrders", "No orders yet.")}
          columns={[
            { key: "order", label: t("portal.order", "Order") },
            {
              key: "status",
              label: t("portal.status", "Status"),
              render: (row) => <PortalStatusChip label={row.status} tone="accent" />,
            },
            {
              key: "payment",
              label: t("checkout.payment", "Payment"),
              render: (row) => <PortalStatusChip label={row.payment} />,
            },
            { key: "total", label: t("cart.total", "Total") },
            { key: "tracking", label: t("portal.tracking", "Tracking") },
            {
              key: "documentId",
              label: "",
              align: "right",
              render: (row) => (
                <PortalButton
                  size="sm"
                  variant="ghost"
                  endIcon="chevron_right"
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/account/orders/${row.documentId}`);
                  }}
                >
                  {t("portal.view", "View")}
                </PortalButton>
              ),
            },
          ]}
          rows={tableRows}
          onRowClick={(row) => navigate(`/account/orders/${row.data.documentId}`)}
        />
      )}
    </CustomerPortalLayout>
  );
}
